<template>
  <!--
    CreateArtifactModal — the "+ New <artifact>" form.

    Fields come from artifactRegistry ARTIFACTS[key].create.fields, so adding a
    field or an artifact is a config edit there, not a change here.

    PROPS  artifact (Object, req)  — the registry entry
           isVisible (Bool, req)
           context (Object)        — shared reference data (metrics, for selects)
           submitting (Bool)       — disables submit mid-POST
    EMITS  submit(values)          — {field: value}; caller builds the payload
           cancel
  -->
  <transition
    enter-active-class="transition ease-out duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition ease-in duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="isVisible"
      class="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-200 p-4"
      @click.self="$emit('cancel')"
    >
      <transition
        enter-active-class="transition ease-out duration-300"
        enter-from-class="opacity-0 scale-95 translate-y-4"
        enter-to-class="opacity-100 scale-100 translate-y-0"
        leave-active-class="transition ease-in duration-200"
        leave-from-class="opacity-100 scale-100 translate-y-0"
        leave-to-class="opacity-0 scale-95 translate-y-4"
      >
        <div
          v-if="isVisible"
          class="bg-white rounded-lg shadow-2xl max-w-lg w-full mx-auto overflow-hidden"
        >
          <!-- Header -->
          <div class="bg-primary-100 px-6 py-4 flex items-center justify-between">
            <div class="flex items-center gap-3 min-w-0">
              <font-awesome-icon
                v-if="artifact.icon"
                :icon="['fas', artifact.icon]"
                class="text-gray-700"
              />
              <h3 class="text-lg font-semibold text-gray-900 truncate">
                New {{ artifact.singular }}
              </h3>
            </div>
            <button
              class="text-gray-600 hover:text-gray-900 px-2"
              aria-label="Close"
              @click="$emit('cancel')"
            >
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
          </div>

          <!-- Fields -->
          <div class="px-6 py-5 space-y-4">
            <div v-for="f in fields" :key="f.name">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ f.label }}
                <span v-if="f.required" class="text-red-dark">*</span>
              </label>

              <textarea
                v-if="f.kind === 'textarea'"
                v-model="values[f.name]"
                :placeholder="f.placeholder || ''"
                rows="3"
                class="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-400 focus:outline-none"
              ></textarea>

              <select
                v-else-if="f.kind === 'select'"
                v-model="values[f.name]"
                class="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-400 focus:outline-none"
              >
                <option v-if="!f.required" :value="null">— none —</option>
                <option v-for="o in optionsFor(f)" :key="o.value" :value="o.value">
                  {{ o.label }}
                </option>
              </select>

              <input
                v-else
                v-model="values[f.name]"
                type="text"
                :placeholder="f.placeholder || ''"
                :class="[
                  'w-full rounded border px-3 py-2 text-sm text-gray-900 focus:outline-none',
                  f.mono ? 'font-mono' : '',
                  'border-gray-300 focus:border-blue-400',
                ]"
                @input="onInput(f)"
                @keydown.enter="maybeSubmit"
              />

              <p v-if="f.help" class="mt-1 text-xs text-gray-400">{{ f.help }}</p>
            </div>

            <p v-if="error" class="text-sm text-red-dark">{{ error }}</p>
          </div>

          <!-- Actions -->
          <div class="px-6 pb-6 flex gap-2 justify-end">
            <button
              class="text-sm text-gray-600 hover:text-gray-900 px-3 py-2 rounded"
              @click="$emit('cancel')"
            >
              Cancel
            </button>
            <button
              class="text-sm bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium px-4 py-2 rounded"
              :disabled="!canSubmit || submitting"
              @click="submit"
            >
              {{ submitting ? 'Creating…' : `Create ${artifact.singular}` }}
            </button>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

/** Slugify to match Django SlugField. */
function slugify(s) {
  return String(s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
}

export default {
  name: 'CreateArtifactModal',
  components: { FontAwesomeIcon },
  props: {
    artifact: { type: Object, required: true },
    isVisible: { type: Boolean, required: true },
    context: { type: Object, default: () => ({}) },
    submitting: { type: Boolean, default: false },
  },
  emits: ['submit', 'cancel'],
  data() {
    return {
      values: {},
      error: null,
      slugTouched: {},
    };
  },
  computed: {
    fields() {
      return (this.artifact.create && this.artifact.create.fields) || [];
    },
    canSubmit() {
      return this.fields
        .filter((f) => f.required)
        .every((f) => {
          const v = this.values[f.name];
          return v !== null && v !== undefined && String(v).trim() !== '';
        });
    },
  },
  watch: {
    isVisible: { immediate: true, handler(v) { if (v) this.reset(); } },
    'artifact.key': function () { if (this.isVisible) this.reset(); },
  },
  methods: {
    reset() {
      const vals = {};
      for (const f of this.fields) {
        vals[f.name] = f.kind === 'select' ? (f.default ?? null) : (f.default ?? '');
      }
      this.values = vals;
      this.slugTouched = {};
      this.error = null;
    },

    optionsFor(f) {
      if (f.optionsFrom === 'metrics') {
        return (this.context.metrics || []).map((m) => ({ value: m.id, label: m.name }));
      }
      return f.options || [];
    },

    onInput(f) {
      if (f.slug) this.slugTouched[f.name] = true;
      // Derive any field declaring slugFrom on this one, until hand-edited.
      for (const other of this.fields) {
        if (other.slugFrom === f.name && !this.slugTouched[other.name]) {
          this.values[other.name] = slugify(this.values[f.name]);
        }
      }
    },

    maybeSubmit() {
      if (this.canSubmit && !this.submitting) this.submit();
    },

    submit() {
      if (!this.canSubmit || this.submitting) return;
      const out = {};
      for (const f of this.fields) {
        let v = this.values[f.name];
        if (typeof v === 'string') v = v.trim();
        out[f.name] = v;
      }
      this.error = null;
      this.$emit('submit', out);
    },
  },
};
</script>
